import React, { useRef, useEffect, useState } from "react";
import { Paper, Button, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import * as dayjs from "dayjs";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { FormItem } from "@/components/Form";
import TagList from "@/components/FormFields/TagList";
import DateRangePicker from "@/components/DateRangePicker";
import {
  scheduleDeviceStatusApi,
  unbindDevicesScheduleTemplatesApi
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import SubmitButton from "@/components/commons/SubmitButton";
import PublishStatus from "@/components/FormFields/PublishStatus";
import { IconFile } from "@/components/Icon";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import ScheduleDeployMembers from "@/components/FormFields/ScheduleDeployMembers";
import ScheduleStatus from "@/components/FormFields/ScheduleStatus";
import ExcuteStatus from "@/components/FormFields/ExcuteStatus";
import ScheduleTasks from "@/components/FormFields/ScheduleTasks";
import { getScheduleTaskType } from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskType";
import ScheduleTemplates from "@/components/FormFields/ScheduleTemplates";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import { ScheduleDeviceListDatatable } from "@/helpers/scheduleDeviceListDatatable";
import groupBy from "lodash/groupBy";
import mapValues from "lodash/mapValues";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";

const ScheduleList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const selectedRowsRef = useRef([]);
  const isReset = useRef(true);
  const operateType = useRef(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const initialValues = {
    template: undefined,
    taskType: undefined,
    publishStatus: undefined,
    scheduleStatus: undefined,
    excuteStatus: undefined,
    group: undefined,
    tag: undefined,
    deployUserId: undefined,
    date: [null, null],
    keywords: ""
  };

  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;
    return {
      scheduleTemplateId: formValues.template,
      taskType: formValues.taskType,
      publishStatus: formValues.publishStatus,
      scheduleStatus: formValues.scheduleStatus ?? -1,
      lastExecutedStatus: formValues.excuteStatus,
      groupIds: formValues.group,
      tagIds: formValues.tag,
      deployUserId: formValues.deployUserId,
      lastExecutedDateStart: date[0] ? dayjs(date[0]).valueOf() : undefined,
      lastExecutedDateEnd: date[1] ? dayjs(date[1]).valueOf() : undefined,
      keywords: formValues.keywords
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable(true);
  };

  const getSelectedRows = () => {
    const selectedIds = tableRef.current.getSelected().map(id => id.split(","));
    const data = tableRef.current?.api().data().toArray();
    const selectedRows = selectedIds.map(id =>
      data.find(
        item =>
          item.deviceId === id[0] &&
          item.scheduleTemplateId === id[1] &&
          item.taskScheduleId === id[2]
      )
    );
    if (selectedRows && selectedRows.length) {
      selectedRowsRef.current = selectedRows;
      return selectedRows;
    }

    toast.info(t({ defaultMessage: "未选择任何设备" }));
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const doEnable = async (rows, enable) => {
    try {
      setLoading(true);
      operateType.current = enable ? "enable" : "disable";
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      // key 是设备id, value 是对应的 schedule_ids
      const params = mapValues(
        groupBy(
          rows.map(row => ({
            deviceId: row.deviceId,
            scheduleId: row.taskScheduleId
          })),
          row => row.deviceId
        ),
        subRow => subRow.map(item => item.scheduleId)
      );
      await scheduleDeviceStatusApi({
        status: enable ? 0 : 1,
        schedule_device_maps: params
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = row => {
    modalConfirm({
      title: t({ defaultMessage: "关闭排程任务" }),
      content: t(
        {
          defaultMessage:
            "是否确认关闭（{name}）该排程任务？关闭后设备将不执行该排程任务。"
        },
        { name: getScheduleTaskType(row.taskType, row.ext) }
      ),
      onConfirm: () => {
        return doEnable(row, false);
      }
    });
  };

  const handleEnable = row => {
    modalConfirm({
      title: t({ defaultMessage: "开启排程任务" }),
      content: t(
        {
          defaultMessage:
            "是否确认开启（{name}）该排程任务？开启后设备需执行该排程任务。"
        },
        { name: getScheduleTaskType(row.taskType, row.ext) }
      ),
      onConfirm: () => {
        return doEnable(row, true);
      }
    });
  };

  const doDelete = async rows => {
    try {
      setLoading(true);
      operateType.current = "delete";
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      // key 是设备id, value 是对应的 template_id
      const params = mapValues(
        groupBy(
          rows.map(row => ({
            deviceId: row.deviceId,
            scheduleTemplateId: row.scheduleTemplateId
          })),
          row => row.deviceId
        ),
        subRow => subRow.map(item => item.scheduleTemplateId)
      );
      await unbindDevicesScheduleTemplatesApi({
        targetType: 0, // 0设备 1门店 2标签
        device_template_maps: params
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除排程模板的套用" }),
      content: t(
        {
          defaultMessage: "是否确认删除（{name}）的套用？"
        },
        { name: row.scheduleTemplateName }
      ),
      onConfirm: () => {
        return doDelete(row);
      }
    });
  };

  const handleBatchDisable = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "关闭排程任务" }),
        content: t({
          defaultMessage: "是否确认关闭排程任务？关闭后设备将不执行该排程模板。"
        }),
        onConfirm: () => {
          return doEnable(selectedRowsRef.current, false);
        }
      });
    }
  };

  const handleBatchEnable = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "开启排程任务" }),
        content: t({
          defaultMessage: "是否确认开启排程任务？开启后设备需执行该排程任务。"
        }),
        onConfirm: () => {
          return doEnable(selectedRowsRef.current, true);
        }
      });
    }
  };

  const handleBatchDelete = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "删除排程模板的套用" }),
        content: t({
          defaultMessage: "是否确认删除排程模板的套用？"
        }),
        onConfirm: () => {
          return doDelete(selectedRowsRef.current);
        }
      });
    }
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]); // eslint-disable-line

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <div className={classes.tableTop}>
              <div>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "设备排程列表" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                >
                  <IconFile size={15} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
              </div>
              <div className={classes.rightAction}>
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                />
              </div>
            </div>
            <CollapsePanel>
              <div className={classes.tableQueryForm}>
                <FormItem
                  label={t({ defaultMessage: "排程模板" })}
                  className={classes.formItem}
                >
                  <ScheduleTemplates
                    name="template"
                    value={values.template}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "排程任务" })}
                  className={classes.formItem}
                >
                  <ScheduleTasks
                    name="taskType"
                    value={values.taskType}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "门店" })}
                  className={classes.formItem}
                >
                  <DeviceGroups
                    name="group"
                    value={values.group}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "标签" })}
                  className={classes.formItem}
                >
                  <TagList
                    name="tag"
                    value={values.tag}
                    onChange={handleChange}
                  />
                </FormItem>
                <CollapseItem>
                  <FormItem
                    label={t({ defaultMessage: "发布状态" })}
                    className={classes.formItem}
                  >
                    <PublishStatus
                      name="publishStatus"
                      schedule
                      value={values.publishStatus}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "排程状态" })}
                    className={classes.formItem}
                  >
                    <ScheduleStatus
                      name="scheduleStatus"
                      value={values.scheduleStatus}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "部署人" })}
                    className={classes.formItem}
                  >
                    <ScheduleDeployMembers
                      name="deployUserId"
                      value={values.deployUserId}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "最后执行时间" })}
                    className={classes.formItem}
                  >
                    <DateRangePicker
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                    />
                  </FormItem>
                  {/* <FormItem
                    label={t({ defaultMessage: "最后执行状态" })}
                    className={classes.formItem}
                  >
                    <ExcuteStatus
                      name="excuteStatus"
                      value={values.excuteStatus}
                      onChange={handleChange}
                    />
                  </FormItem> */}
                </CollapseItem>
                <FormItem className={classes.formItem}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginRight: 8 }}
                  >
                    {t({ defaultMessage: "查询" })}
                  </Button>
                  <Button variant="outlined" onClick={resetQuery}>
                    {t({ defaultMessage: "重置" })}
                  </Button>
                  <CollapseTrigger />
                </FormItem>
              </div>
            </CollapsePanel>
          </div>
        </form>
        <ScheduleDeviceListDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onDelete={handleDelete}
          onEnable={handleEnable}
          onDisable={handleDisable}
          onLoaded={handleLoaded}
          footer={
            <>
              <HasPermission permission="schedule_task_list_task_enable_disable">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "enable"}
                  onClick={handleBatchEnable}
                >
                  {t({ defaultMessage: "批量开启" })}
                </SubmitButton>
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "disable"}
                  onClick={handleBatchDisable}
                >
                  {t({ defaultMessage: "批量关闭" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="schedule_task_list_task_enable_disable">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "delete"}
                  onClick={handleBatchDelete}
                >
                  {t({ defaultMessage: "批量删除" })}
                </SubmitButton>
              </HasPermission>
            </>
          }
        />
      </Paper>
    </div>
  );
};

export default ScheduleList;
